/* ==========================================================================
   SPOTKICK Homepage — scoped behaviour
   Depends on Motion's theme.js (loaded first, both deferred):
     theme.AjaxProduct  -> POSTs /cart/add.js, fires ajaxProduct:added
     theme.CartDrawer   -> already listens for that event, rebuilds + opens
   We add button state, tabs, poster-first media and rails. No cart code.
   ========================================================================== */
(function () {
  'use strict';

  // Idempotent: the assets snippet may render more than once per page.
  if (window.SKHome) return;

  var SKHome = (window.SKHome = {});

  var ADDED_MS = 1600;

  /* ----------------------------------------------------------------------
     Utilities
     -------------------------------------------------------------------- */
  function $$(sel, root) {
    return Array.prototype.slice.call((root || document).querySelectorAll(sel));
  }

  function once(el, key) {
    if (el.dataset[key]) return false;
    el.dataset[key] = '1';
    return true;
  }

  function prefersReducedData() {
    var c = navigator.connection;
    if (!c) return false;
    if (c.saveData) return true;
    return ['slow-2g', '2g', '3g'].indexOf(c.effectiveType) !== -1;
  }

  /* ----------------------------------------------------------------------
     Quick Add (brief §8)
     Every buy control on the homepage is a real <form> handled by
     theme.AjaxProduct. This only manages the button's visible state.
     -------------------------------------------------------------------- */
  SKHome.initQuickAdd = function (root) {
    if (typeof theme === 'undefined' || !theme.AjaxProduct) return;

    $$('[data-sk-atc-form]', root).forEach(function (form) {
      if (!once(form, 'skAtcBound')) return;

      var uid = form.getAttribute('data-sk-atc-form');
      var btn = form.querySelector('[data-atc]');
      var errorEl = form.querySelector('[data-atc-error]');
      var resetTimer = null;

      if (!btn) return;

      var watchdog = null;

      function clearError() {
        form.classList.remove('has-error');
        if (errorEl) errorEl.textContent = '';
      }

      function reset() {
        window.clearTimeout(watchdog);
        btn.classList.remove('is-loading', 'is-added', 'btn--loading');
        btn.disabled = false;
      }

      // Registered BEFORE theme.AjaxProduct so it always runs, and paired
      // with a watchdog: theme.AjaxProduct silently no-ops when another add
      // is already in flight, which would otherwise strand the button.
      form.addEventListener('submit', function () {
        if (btn.disabled) return;
        clearError();
        window.clearTimeout(resetTimer);
        btn.classList.remove('is-added');
        btn.classList.add('is-loading');
        btn.disabled = true;
        watchdog = window.setTimeout(reset, 8000);
      });

      new theme.AjaxProduct(form, '[data-atc]', { scopedEventId: uid });

      document.addEventListener('ajaxProduct:added:' + uid, function () {
        window.clearTimeout(watchdog);
        btn.classList.remove('is-loading', 'btn--loading');
        btn.classList.add('is-added');
        // theme.CartDrawer has already rebuilt and opened itself.
        resetTimer = window.setTimeout(reset, ADDED_MS);
      });

      document.addEventListener('ajaxProduct:error:' + uid, function (evt) {
        reset();
        if (errorEl) {
          errorEl.textContent =
            (evt.detail && evt.detail.errorMessage) ||
            'Sorry, that could not be added to your cart. Please try again.';
          form.classList.add('has-error');
        }
      });

      // Motion appends its own .errors node on failure; we render our own.
      var strayErrors = form.querySelector('.errors');
      if (strayErrors) strayErrors.remove();
    });
  };

  /* ----------------------------------------------------------------------
     Quantity selectors — reuse the theme's widget where available
     -------------------------------------------------------------------- */
  SKHome.initQuantity = function (root) {
    if (typeof theme === 'undefined' || !theme.QtySelector) return;
    // theme.QtySelector binds to the .js-qty__wrapper the theme snippet emits
    $$('[data-sk-qty] .js-qty__wrapper', root).forEach(function (el) {
      if (!once(el, 'skQtyBound')) return;
      new theme.QtySelector(el, { namespace: '.sk-home' });
    });
  };

  /* ----------------------------------------------------------------------
     Tabs — WAI-ARIA tablist, shared by comparison / how-to-play / benefits
     Panels are pre-rendered and toggled. No fetch, no layout shift.
     -------------------------------------------------------------------- */
  SKHome.initTabs = function (root) {
    $$('[data-sk-tabs]', root).forEach(function (group) {
      if (!once(group, 'skTabsBound')) return;

      var tabs = $$('[role="tab"]', group);
      if (!tabs.length) return;

      // Comparison columns are a tab interface on mobile only; on desktop
      // every column is visible at once and there is no tablist at all.
      var mobileOnly = group.hasAttribute('data-sk-tabs-mobile-only');
      var mql = window.matchMedia('(max-width: 768px)');
      var current = null;

      function panelFor(tab) {
        return document.getElementById(tab.getAttribute('aria-controls'));
      }

      function select(tab, focus) {
        current = tab;

        if (mobileOnly && !mql.matches) {
          revealAll();
          return;
        }

        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.setAttribute('tabindex', on ? '0' : '-1');
          var panel = panelFor(t);
          if (panel) panel.hidden = !on;
        });
        if (focus) tab.focus();

        group.dispatchEvent(
          new CustomEvent('sk:tabchange', {
            detail: { value: tab.getAttribute('data-sk-tab') },
            bubbles: true
          })
        );
      }

      // Desktop for a mobile-only tablist: drop the tab semantics entirely
      // rather than leaving panels marked hidden while they are on screen.
      function revealAll() {
        group.hidden = true;
        tabs.forEach(function (t) {
          var panel = panelFor(t);
          if (!panel) return;
          panel.hidden = false;
          panel.removeAttribute('role');
          panel.removeAttribute('aria-labelledby');
        });
      }

      function restoreTabs() {
        group.hidden = false;
        tabs.forEach(function (t) {
          var panel = panelFor(t);
          if (!panel) return;
          panel.setAttribute('role', 'tabpanel');
          panel.setAttribute('aria-labelledby', t.id);
        });
        select(current || tabs[0], false);
      }

      function applyMode() {
        if (!mobileOnly) return;
        if (mql.matches) restoreTabs();
        else revealAll();
      }

      if (mobileOnly) {
        if (mql.addEventListener) mql.addEventListener('change', applyMode);
        else if (mql.addListener) mql.addListener(applyMode);
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          select(tab, false);
        });
        tab.addEventListener('keydown', function (evt) {
          var i = tabs.indexOf(tab);
          var next = null;
          if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') next = tabs[(i + 1) % tabs.length];
          else if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') next = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (evt.key === 'Home') next = tabs[0];
          else if (evt.key === 'End') next = tabs[tabs.length - 1];
          if (next) {
            evt.preventDefault();
            select(next, true);
          }
        });
      });

      // Establish the initial state from markup
      var initial = tabs.filter(function (t) {
        return t.getAttribute('aria-selected') === 'true';
      })[0];
      current = initial || tabs[0];

      if (mobileOnly) applyMode();
      else select(current, false);
    });
  };

  /* ----------------------------------------------------------------------
     Poster-first video (brief §5.1, §12, §19)
     No <video> element ships in the HTML. One is built on demand, and
     only one plays at a time — six cards can never all download.
     -------------------------------------------------------------------- */
  var activeVideo = null;

  function teardownActive() {
    if (!activeVideo) return;
    var host = activeVideo;
    var el = host.querySelector('.sk-media__video');
    if (el) el.remove();
    var trigger = host.querySelector('[data-sk-play]');
    if (trigger) trigger.classList.remove('is-hidden');
    activeVideo = null;
  }

  function buildVideo(host) {
    var src = host.getAttribute('data-video-src');
    var embed = host.getAttribute('data-video-embed');
    if (!src && !embed) return null;

    var el;
    if (embed) {
      el = document.createElement('iframe');
      el.src = embed + (embed.indexOf('?') === -1 ? '?' : '&') + 'autoplay=1';
      el.setAttribute('allow', 'autoplay; encrypted-media; picture-in-picture');
      el.setAttribute('allowfullscreen', '');
      el.setAttribute('title', host.getAttribute('data-video-title') || 'SPOTKICK video');
    } else {
      el = document.createElement('video');
      el.src = src;
      el.preload = 'none';
      el.controls = true;
      el.playsInline = true;
      el.setAttribute('playsinline', '');
      if (host.getAttribute('data-video-loop') === 'true') el.loop = true;
      if (host.getAttribute('data-video-muted') === 'true') {
        el.muted = true;
        el.setAttribute('muted', '');
      }
      var poster = host.getAttribute('data-video-poster');
      if (poster) el.poster = poster;
    }
    el.className = 'sk-media__video';
    return el;
  }

  SKHome.playVideo = function (host) {
    if (!host || host.querySelector('.sk-media__video')) return;
    if (activeVideo && activeVideo !== host) teardownActive();

    var el = buildVideo(host);
    if (!el) return;

    host.appendChild(el);
    activeVideo = host;

    var trigger = host.querySelector('[data-sk-play]');
    if (trigger) trigger.classList.add('is-hidden');

    if (el.tagName === 'VIDEO') {
      var p = el.play();
      if (p && p.catch) p.catch(function () { /* autoplay refused — controls remain */ });
    }
  };

  SKHome.initVideo = function (root) {
    $$('[data-sk-video]', root).forEach(function (host) {
      if (!once(host, 'skVideoBound')) return;

      var trigger = host.querySelector('[data-sk-play]');
      if (trigger) {
        trigger.addEventListener('click', function () {
          SKHome.playVideo(host);
        });
      }

      // Ambient autoplay is opt-in, near-viewport only, and never on
      // save-data or slow connections. Otherwise it stays poster-only.
      if (host.getAttribute('data-video-auto') !== 'true') return;
      if (prefersReducedData()) return;
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      if (!('IntersectionObserver' in window)) return;

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            io.disconnect();
            SKHome.playVideo(host);
          });
        },
        { rootMargin: '400px 0px' }
      );
      io.observe(host);
    });
  };

  /* ----------------------------------------------------------------------
     Rails — arrow buttons for the CSS scroll-snap sliders
     -------------------------------------------------------------------- */
  SKHome.initRails = function (root) {
    $$('[data-sk-rail-group]', root).forEach(function (group) {
      if (!once(group, 'skRailBound')) return;

      var rail = group.querySelector('[data-sk-rail]');
      if (!rail) return;

      var prev = group.querySelector('[data-sk-rail-prev]');
      var next = group.querySelector('[data-sk-rail-next]');
      var dotsHost = group.querySelector('[data-sk-rail-dots]');

      var cards = Array.prototype.filter.call(rail.children, function (el) {
        return el.nodeType === 1;
      });

      var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      function behavior() { return reduceMotion.matches ? 'auto' : 'smooth'; }

      function maxScroll() { return rail.scrollWidth - rail.clientWidth; }
      function overflowing() { return maxScroll() > 8; }

      function step() {
        var first = rail.firstElementChild;
        return first ? first.getBoundingClientRect().width + 24 : rail.clientWidth * 0.8;
      }

      /* --- index helpers ------------------------------------------------ */
      function activeIndex() {
        var mid = rail.scrollLeft + rail.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        cards.forEach(function (card, i) {
          var c = card.offsetLeft - rail.offsetLeft + card.offsetWidth / 2;
          var d = Math.abs(c - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        return best;
      }

      function goTo(i) {
        var card = cards[i];
        if (!card) return;
        rail.scrollTo({ left: card.offsetLeft - rail.offsetLeft, behavior: behavior() });
      }

      /* --- dots --------------------------------------------------------- */
      var dots = [];
      if (dotsHost && cards.length > 1) {
        dotsHost.setAttribute('role', 'tablist');
        dotsHost.setAttribute('aria-label', 'Slides');
        dots = cards.map(function (card, i) {
          var b = document.createElement('button');
          b.type = 'button';
          b.className = 'sk-rail-dot';
          b.setAttribute('aria-label', 'Go to slide ' + (i + 1) + ' of ' + cards.length);
          b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
          b.addEventListener('click', function () { stopAuto(); goTo(i); });
          dotsHost.appendChild(b);
          return b;
        });
      }

      function sync() {
        var on = overflowing();
        if (prev && next) {
          // With looping enabled the arrows are never dead ends
          prev.disabled = !on;
          next.disabled = !on;
        }
        if (dotsHost) dotsHost.classList.toggle('is-visible', on && cards.length > 1);
        if (!dots.length) return;
        var idx = activeIndex();
        dots.forEach(function (d, i) {
          d.setAttribute('aria-current', i === idx ? 'true' : 'false');
        });
      }

      /* --- autoplay ----------------------------------------------------- */
      var intervalMs = (parseFloat(group.getAttribute('data-sk-autoplay')) || 0) * 1000;
      var timer = null;
      var killed = false;
      var inView = true;

      function advance() {
        if (!overflowing()) return;
        // Loop: at the end, glide back to the first card
        if (rail.scrollLeft >= maxScroll() - 4) goTo(0);
        else rail.scrollBy({ left: step(), behavior: behavior() });
      }

      function startAuto() {
        if (killed || timer || !intervalMs || reduceMotion.matches) return;
        if (!inView || document.hidden || !overflowing()) return;
        timer = window.setInterval(advance, intervalMs);
      }
      function pauseAuto() {
        if (!timer) return;
        window.clearInterval(timer);
        timer = null;
      }
      function stopAuto() {
        killed = true;
        pauseAuto();
      }

      // Any genuine interaction ends autoplay for good
      ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach(function (evt) {
        rail.addEventListener(evt, stopAuto, { passive: true });
      });
      rail.addEventListener('focusin', stopAuto);
      // Playing a video should never be interrupted by the carousel moving
      group.addEventListener('click', function (e) {
        if (e.target.closest && e.target.closest('[data-sk-play]')) stopAuto();
      });

      if (prev) prev.addEventListener('click', function () {
        stopAuto();
        if (rail.scrollLeft <= 4) rail.scrollTo({ left: maxScroll(), behavior: behavior() });
        else rail.scrollBy({ left: -step(), behavior: behavior() });
      });
      if (next) next.addEventListener('click', function () {
        stopAuto();
        if (rail.scrollLeft >= maxScroll() - 4) goTo(0);
        else rail.scrollBy({ left: step(), behavior: behavior() });
      });

      // Pause off-screen and on hidden tabs so it costs nothing in the background
      document.addEventListener('visibilitychange', function () {
        if (document.hidden) pauseAuto(); else startAuto();
      });
      if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting;
            if (inView) startAuto(); else pauseAuto();
          });
        }, { threshold: 0.25 }).observe(group);
      }

      var ticking = false;
      rail.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () { sync(); ticking = false; });
      }, { passive: true });
      window.addEventListener('resize', sync, { passive: true });

      sync();
      window.setTimeout(function () { sync(); startAuto(); }, 300);
    });
  };

  /* ----------------------------------------------------------------------
     Mobile sliders
     Card grids turn into swipe sliders under 769px (CSS does the layout).
     This adds dot navigation and keyboard access, and only when the track
     genuinely overflows — a one-card section gets no furniture.
     -------------------------------------------------------------------- */
  SKHome.initMobileSliders = function (root) {
    $$('[data-sk-mslider]', root).forEach(function (track) {
      if (!once(track, 'skMsBound')) return;

      var cards = Array.prototype.filter.call(track.children, function (el) {
        return el.nodeType === 1 && !el.classList.contains('sk-ms-nav');
      });
      if (cards.length < 2) return;

      var nav = document.createElement('div');
      nav.className = 'sk-ms-nav';
      nav.setAttribute('role', 'tablist');
      nav.setAttribute('aria-label', (track.getAttribute('aria-label') || 'Items') + ' navigation');

      var dots = cards.map(function (card, i) {
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'sk-ms-dot';
        b.setAttribute('aria-label', 'Go to item ' + (i + 1) + ' of ' + cards.length);
        b.setAttribute('aria-current', i === 0 ? 'true' : 'false');
        b.addEventListener('click', function () {
          track.scrollTo({
            left: card.offsetLeft - track.offsetLeft,
            behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
          });
        });
        nav.appendChild(b);
        return b;
      });

      if (track.parentNode) track.parentNode.insertBefore(nav, track.nextSibling);

      function overflowing() {
        return track.scrollWidth - track.clientWidth > 8;
      }

      function sync() {
        var on = overflowing();
        nav.classList.toggle('is-visible', on);

        // Keyboard users can only scroll a region that is focusable
        if (on) {
          track.setAttribute('tabindex', '0');
          track.setAttribute('role', 'group');
        } else {
          track.removeAttribute('tabindex');
          track.removeAttribute('role');
        }
        if (!on) return;

        // Nearest card to the centre of the viewport wins
        var mid = track.scrollLeft + track.clientWidth / 2;
        var best = 0;
        var bestDist = Infinity;
        cards.forEach(function (card, i) {
          var c = card.offsetLeft - track.offsetLeft + card.offsetWidth / 2;
          var d = Math.abs(c - mid);
          if (d < bestDist) { bestDist = d; best = i; }
        });
        dots.forEach(function (d, i) {
          d.setAttribute('aria-current', i === best ? 'true' : 'false');
        });
      }

      var ticking = false;
      track.addEventListener('scroll', function () {
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () { sync(); ticking = false; });
      }, { passive: true });

      window.addEventListener('resize', sync, { passive: true });

      // Fonts and images settle after first paint — re-measure once they do
      sync();
      window.setTimeout(sync, 300);
      if (window.ResizeObserver) {
        var ro = new ResizeObserver(sync);
        ro.observe(track);
      }
    });
  };

  /* ----------------------------------------------------------------------
     Product switcher — comparison, featured gallery, final CTA
     Swaps pre-rendered panels and the hidden variant id. No reload.
     -------------------------------------------------------------------- */
  SKHome.initSwitcher = function (root) {
    $$('[data-sk-switcher]', root).forEach(function (scope) {
      if (!once(scope, 'skSwitcherBound')) return;

      scope.addEventListener('sk:tabchange', function (evt) {
        var value = evt.detail && evt.detail.value;
        if (!value) return;

        $$('[data-sk-switch-target]', scope).forEach(function (target) {
          var match = target.getAttribute('data-sk-switch-target') === value;
          target.hidden = !match;
          target.classList.toggle('is-active', match);
        });

        // Any video that was playing belongs to the panel we just left
        teardownActive();
      });
    });
  };

  /* ----------------------------------------------------------------------
     Gallery — thumbnail rail driving one large media stage (brief §10)
     -------------------------------------------------------------------- */
  SKHome.initGallery = function (root) {
    $$('[data-sk-gallery]', root).forEach(function (gallery) {
      if (!once(gallery, 'skGalleryBound')) return;

      var thumbs = $$('[data-sk-thumb]', gallery);
      var stages = $$('[data-sk-stage]', gallery);
      if (!thumbs.length) return;

      var stageRail = gallery.querySelector('[data-sk-stage-rail]');

      // Visibility is a class, not the `hidden` attribute: on mobile every
      // stage is genuinely on screen inside a swipe rail, so marking the
      // off-screen ones hidden would lie to assistive tech.
      function show(id) {
        stages.forEach(function (stage) {
          stage.classList.toggle('is-active', stage.getAttribute('data-sk-stage') === id);
        });
        thumbs.forEach(function (thumb) {
          thumb.setAttribute(
            'aria-current',
            thumb.getAttribute('data-sk-thumb') === id ? 'true' : 'false'
          );
        });
        teardownActive();
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var id = thumb.getAttribute('data-sk-thumb');
          show(id);
          // On mobile the stages are a rail — scroll to the chosen one
          if (stageRail && window.matchMedia('(max-width: 768px)').matches) {
            var target = stages.filter(function (s) {
              return s.getAttribute('data-sk-stage') === id;
            })[0];
            if (target) {
              stageRail.scrollTo({ left: target.offsetLeft - stageRail.offsetLeft, behavior: 'smooth' });
            }
          }
        });
      });

      // Mobile: keep thumbs in sync as the rail is swiped
      if (stageRail && 'IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.intersectionRatio < 0.6) return;
              var id = entry.target.getAttribute('data-sk-stage');
              thumbs.forEach(function (thumb) {
                thumb.setAttribute(
                  'aria-current',
                  thumb.getAttribute('data-sk-thumb') === id ? 'true' : 'false'
                );
              });
            });
          },
          { root: stageRail, threshold: 0.6 }
        );
        stages.forEach(function (s) { io.observe(s); });
      }
    });
  };

  /* ----------------------------------------------------------------------
     Accordion — FAQ-style, no layout jump, keyboard operable
     -------------------------------------------------------------------- */
  SKHome.initAccordion = function (root) {
    $$('[data-sk-accordion]', root).forEach(function (group) {
      if (!once(group, 'skAccBound')) return;

      $$('[data-sk-acc-trigger]', group).forEach(function (trigger) {
        trigger.addEventListener('click', function () {
          var panel = document.getElementById(trigger.getAttribute('aria-controls'));
          var open = trigger.getAttribute('aria-expanded') === 'true';
          trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
          if (panel) panel.hidden = open;
        });
      });
    });
  };

  /* ----------------------------------------------------------------------
     Sticky mobile buy bar
     Shows once the hero has scrolled out of view. Phones only — the CSS
     hides it above 768px, and this bails early so desktop pays nothing.
     -------------------------------------------------------------------- */
  SKHome.initStickyBar = function (root) {
    $$('[data-sk-stickybar]', root).forEach(function (bar) {
      if (!once(bar, 'skBarBound')) return;

      var mql = window.matchMedia('(max-width: 768px)');
      var hero = document.getElementById(bar.getAttribute('data-hero'));
      if (!hero || !('IntersectionObserver' in window)) return;

      // Move it to <body> so no ancestor's transform or overflow can trap it
      if (bar.parentNode !== document.body) document.body.appendChild(bar);

      function enable() {
        bar.classList.add('is-ready');
        document.body.classList.add('sk-has-stickybar');
      }
      function disable() {
        bar.classList.remove('is-ready', 'is-visible');
        document.body.classList.remove('sk-has-stickybar');
      }

      var io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!mql.matches) return;
            bar.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
          });
        },
        // The extended bottom margin counts "still below the screen" as intersecting,
        // so jumping straight past the hero still flips the state.
        { threshold: 0, rootMargin: '0px 0px 100000px 0px' }
      );

      function apply() {
        if (mql.matches) { enable(); io.observe(hero); }
        else { disable(); io.unobserve(hero); }
      }

      if (mql.addEventListener) mql.addEventListener('change', apply);
      else if (mql.addListener) mql.addListener(apply);
      apply();
    });
  };

  /* ----------------------------------------------------------------------
     Scroll reveal — section heads and card grids marked [data-sk-reveal]
     fade/lift into view once, then unobserve. Pure progressive enhancement:
     CSS only hides them once this arms `.sk-reveal-armed` on <html>, so
     without JS, without IntersectionObserver, or with reduced motion
     requested, everything just renders visible and static.
     -------------------------------------------------------------------- */
  SKHome.initReveal = function (root) {
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.documentElement.classList.add('sk-reveal-armed');

    var targets = $$('[data-sk-reveal]', root).filter(function (el) {
      return once(el, 'skRevealBound');
    });
    if (!targets.length) return;

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          io.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -10% 0px', threshold: 0.15 }
    );
    targets.forEach(function (el) { io.observe(el); });
  };

  /* ----------------------------------------------------------------------
     Boot
     -------------------------------------------------------------------- */
  SKHome.init = function (root) {
    root = root || document;
    SKHome.initQuickAdd(root);
    SKHome.initQuantity(root);
    SKHome.initTabs(root);
    SKHome.initSwitcher(root);
    SKHome.initVideo(root);
    SKHome.initRails(root);
    SKHome.initMobileSliders(root);
    SKHome.initGallery(root);
    SKHome.initAccordion(root);
    SKHome.initStickyBar(root);
    SKHome.initReveal(root);
  };

  function boot() {
    SKHome.init(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Theme editor: re-init a section when it is added or redrawn
  document.addEventListener('shopify:section:load', function (evt) {
    SKHome.init(evt.target);
  });
  document.addEventListener('shopify:section:select', function (evt) {
    SKHome.init(evt.target);
  });
})();
